/**
 * ASCII animation engine — TypeScript port of the Rust dd-ascii-anim crate.
 *
 * Provides a frame buffer, layer compositor, diff engine, and staggered glyph
 * rotation for rendering DOS-style ASCII animations on a canvas element.
 *
 * Modules ported:
 *   buffer.rs   -> Color, Cell, FrameBuffer
 *   compositor.rs -> LayerKind, Layer, LayerCompositor
 *   diff.rs     -> CellDiff, DiffEngine
 *   glyph.rs    -> GlyphConfig, GlyphRotator (+ internal Rng, CellState)
 */

// ---------------------------------------------------------------------------
// buffer.rs — Color, Cell, FrameBuffer
// ---------------------------------------------------------------------------

/** A color for foreground or background, stored as RGB bytes. */
export class Color {
  constructor(
    public readonly r: number,
    public readonly g: number,
    public readonly b: number,
  ) {}

  static white(): Color {
    return new Color(255, 255, 255);
  }

  static black(): Color {
    return new Color(0, 0, 0);
  }

  static green(): Color {
    return new Color(0, 255, 0);
  }

  static red(): Color {
    return new Color(255, 0, 0);
  }

  static amber(): Color {
    return new Color(255, 191, 0);
  }

  equals(other: Color): boolean {
    return this.r === other.r && this.g === other.g && this.b === other.b;
  }
}

/** A single cell in the frame buffer. */
export interface Cell {
  ch: string;
  fg: Color;
  bg: Color;
  dirty: boolean;
}

/** Create a default cell (space, white on black, dirty). */
function defaultCell(): Cell {
  return { ch: " ", fg: Color.white(), bg: Color.black(), dirty: true };
}

/**
 * A 2D grid of Cells for rendering.
 *
 * The buffer uses row-major order: cells[row][col]. All newly created buffers
 * have every cell marked dirty so the first frame renders completely.
 */
export class FrameBuffer {
  private cells: Cell[][];
  private readonly _width: number;
  private readonly _height: number;

  constructor(width: number, height: number) {
    this._width = width;
    this._height = height;
    this.cells = [];
    for (let r = 0; r < height; r++) {
      const row: Cell[] = [];
      for (let c = 0; c < width; c++) {
        row.push(defaultCell());
      }
      this.cells.push(row);
    }
  }

  /** Get the buffer dimensions as [width, height]. */
  dimensions(): [number, number] {
    return [this._width, this._height];
  }

  /** Get a reference to the cell at (row, col), or undefined if out of bounds. */
  get(row: number, col: number): Cell | undefined {
    return this.cells[row]?.[col];
  }

  /**
   * Set a cell's character and colors, marking it dirty only if the value
   * actually changed. Out-of-bounds writes are silently ignored.
   */
  set(row: number, col: number, ch: string, fg: Color, bg: Color): void {
    const cell = this.cells[row]?.[col];
    if (!cell) return;
    if (cell.ch !== ch || !cell.fg.equals(fg) || !cell.bg.equals(bg)) {
      cell.ch = ch;
      cell.fg = fg;
      cell.bg = bg;
      cell.dirty = true;
    }
  }

  /** Clear all dirty flags across the buffer. */
  clearDirty(): void {
    for (const row of this.cells) {
      for (const cell of row) {
        cell.dirty = false;
      }
    }
  }

  /** Collect all dirty cells as {row, col, cell} tuples. */
  dirtyCells(): Array<{ row: number; col: number; cell: Cell }> {
    const result: Array<{ row: number; col: number; cell: Cell }> = [];
    for (let r = 0; r < this._height; r++) {
      for (let c = 0; c < this._width; c++) {
        const cell = this.cells[r][c];
        if (cell.dirty) {
          result.push({ row: r, col: c, cell });
        }
      }
    }
    return result;
  }

  /** Count the number of dirty cells without allocating. */
  dirtyCount(): number {
    let count = 0;
    for (const row of this.cells) {
      for (const cell of row) {
        if (cell.dirty) count++;
      }
    }
    return count;
  }

  /** Fill the entire buffer with a single character and color pair. All cells are marked dirty. */
  fill(ch: string, fg: Color, bg: Color): void {
    for (const row of this.cells) {
      for (const cell of row) {
        cell.ch = ch;
        cell.fg = fg;
        cell.bg = bg;
        cell.dirty = true;
      }
    }
  }

  /**
   * Write a string horizontally starting at (row, col).
   * Characters that would fall outside the buffer are clipped.
   * Returns the number of characters actually written.
   */
  writeStr(row: number, col: number, text: string, fg: Color, bg: Color): number {
    let written = 0;
    for (let i = 0; i < text.length; i++) {
      const targetCol = col + i;
      if (targetCol >= this._width || row >= this._height) break;
      this.set(row, targetCol, text[i], fg, bg);
      written++;
    }
    return written;
  }

  /**
   * Draw a rectangular region filled with a character.
   * The rectangle is clipped to the buffer bounds.
   */
  fillRect(
    top: number,
    left: number,
    width: number,
    height: number,
    ch: string,
    fg: Color,
    bg: Color,
  ): void {
    const rowEnd = Math.min(top + height, this._height);
    const colEnd = Math.min(left + width, this._width);
    for (let r = top; r < rowEnd; r++) {
      for (let c = left; c < colEnd; c++) {
        this.set(r, c, ch, fg, bg);
      }
    }
  }

  /** Reset all cells to default (space, white on black, dirty). */
  clear(): void {
    this.fill(" ", Color.white(), Color.black());
  }

  /** Create a deep copy of this buffer. All cells in the clone are marked dirty. */
  clone(): FrameBuffer {
    const buf = new FrameBuffer(this._width, this._height);
    for (let r = 0; r < this._height; r++) {
      for (let c = 0; c < this._width; c++) {
        const cell = this.cells[r][c];
        buf.set(r, c, cell.ch, cell.fg, cell.bg);
      }
    }
    return buf;
  }
}

// ---------------------------------------------------------------------------
// compositor.rs — LayerKind, Layer, LayerCompositor
// ---------------------------------------------------------------------------

/** A layer type for the compositor, determining compositing order. */
export enum LayerKind {
  /** Border/scroll decoration (bottom layer). */
  Border = "Border",
  /** Character sprite (middle layer). */
  Sprite = "Sprite",
  /** Background glyph rotation (top layer, transparent). */
  Glyph = "Glyph",
}

/** A single named layer with its own frame buffer and visibility toggle. */
export interface Layer {
  kind: LayerKind;
  buffer: FrameBuffer;
  visible: boolean;
}

/**
 * Composites multiple Layers into a final output FrameBuffer.
 *
 * Layers are composited in insertion order (first added = bottom). On upper
 * layers, the space character (' ') is treated as transparent — it does not
 * overwrite whatever the lower layer painted. The Border layer is an exception:
 * its spaces are opaque.
 */
export class LayerCompositor {
  private layers: Layer[] = [];
  private _output: FrameBuffer;

  constructor(width: number, height: number) {
    this._output = new FrameBuffer(width, height);
  }

  /** Add a layer with its own buffer dimensions. Layers composite in add order (first = bottom). */
  addLayer(kind: LayerKind, width: number, height: number): void {
    this.layers.push({
      kind,
      buffer: new FrameBuffer(width, height),
      visible: true,
    });
  }

  /** Get a mutable reference to a layer by kind, or undefined if not found. */
  layerMut(kind: LayerKind): Layer | undefined {
    return this.layers.find((l) => l.kind === kind);
  }

  /** Get an immutable reference to a layer by kind. */
  layer(kind: LayerKind): Layer | undefined {
    return this.layers.find((l) => l.kind === kind);
  }

  /** Toggle visibility of a layer. Returns true if the layer was found and toggled. */
  setVisible(kind: LayerKind, visible: boolean): boolean {
    const layer = this.layerMut(kind);
    if (layer) {
      layer.visible = visible;
      return true;
    }
    return false;
  }

  /**
   * Composite all visible layers into the output buffer.
   *
   * Iterates bottom-to-top. On non-border layers, space characters are
   * treated as transparent and do not overwrite the cell below.
   */
  composite(): void {
    const [w, h] = this._output.dimensions();
    for (let r = 0; r < h; r++) {
      for (let c = 0; c < w; c++) {
        let finalCell = defaultCell();
        for (const layer of this.layers) {
          if (!layer.visible) continue;
          const cell = layer.buffer.get(r, c);
          if (cell) {
            // Space = transparent on non-border layers
            if (cell.ch !== " " || layer.kind === LayerKind.Border) {
              finalCell = {
                ch: cell.ch,
                fg: cell.fg,
                bg: cell.bg,
                dirty: cell.dirty,
              };
            }
          }
        }
        this._output.set(r, c, finalCell.ch, finalCell.fg, finalCell.bg);
      }
    }
  }

  /** Get the composited output buffer. */
  output(): FrameBuffer {
    return this._output;
  }

  /** Get the number of layers. */
  layerCount(): number {
    return this.layers.length;
  }
}

// ---------------------------------------------------------------------------
// diff.rs — CellDiff, DiffEngine
// ---------------------------------------------------------------------------

/** A single cell change, ready to be sent to the renderer. */
export interface CellDiff {
  row: number;
  col: number;
  ch: string;
  fg: Color;
  bg: Color;
}

/**
 * Computes diffs from dirty flags in a FrameBuffer.
 */
export class DiffEngine {
  /**
   * Compute all dirty cells from a buffer as a diff list.
   * Each dirty cell becomes a CellDiff with its position and current value.
   * The caller should call buffer.clearDirty() after consuming the diff.
   */
  static compute(buffer: FrameBuffer): CellDiff[] {
    return buffer.dirtyCells().map(({ row, col, cell }) => ({
      row,
      col,
      ch: cell.ch,
      fg: cell.fg,
      bg: cell.bg,
    }));
  }

  /**
   * Compute dirty cells only within a rectangular region.
   * Useful for partial updates when only a specific area may have changed.
   */
  static computeRegion(
    buffer: FrameBuffer,
    top: number,
    left: number,
    width: number,
    height: number,
  ): CellDiff[] {
    const [bufW, bufH] = buffer.dimensions();
    const rowEnd = Math.min(top + height, bufH);
    const colEnd = Math.min(left + width, bufW);
    const result: CellDiff[] = [];
    for (let r = top; r < rowEnd; r++) {
      for (let c = left; c < colEnd; c++) {
        const cell = buffer.get(r, c);
        if (cell && cell.dirty) {
          result.push({
            row: r,
            col: c,
            ch: cell.ch,
            fg: cell.fg,
            bg: cell.bg,
          });
        }
      }
    }
    return result;
  }
}

/**
 * Apply a list of diffs to a frame buffer.
 * Useful for replaying recorded diffs or testing round-trip consistency.
 */
export function applyDiffs(buffer: FrameBuffer, diffs: CellDiff[]): void {
  for (const diff of diffs) {
    buffer.set(diff.row, diff.col, diff.ch, diff.fg, diff.bg);
  }
}

// ---------------------------------------------------------------------------
// glyph.rs — GlyphConfig, GlyphRotator (+ internal Rng, CellState)
// ---------------------------------------------------------------------------

/** Simple deterministic PRNG (xorshift64) for reproducible animations. */
class Rng {
  private state: bigint;

  constructor(seed: number) {
    // Avoid zero state which would produce all zeros
    this.state = seed === 0 ? 1n : BigInt(seed) & 0xffffffffffffffffn;
  }

  /** Generate the next pseudo-random u64 (as bigint). */
  private nextU64(): bigint {
    let x = this.state;
    x ^= (x << 13n) & 0xffffffffffffffffn;
    x ^= x >> 7n;
    x ^= (x << 17n) & 0xffffffffffffffffn;
    this.state = x;
    return x;
  }

  /** Generate a value in [0, max). Returns 0 if max is 0. */
  nextBounded(max: number): number {
    if (max === 0) return 0;
    const val = this.nextU64() % BigInt(max);
    return Number(val);
  }

  /** Generate a value in [0, 100). */
  nextPercent(): number {
    return this.nextBounded(100);
  }
}

/** Per-cell rotation state. */
interface CellState {
  /** When this cell should next rotate (milliseconds). */
  nextRotateAt: number;
  /** Current index into the glyph palette. */
  paletteIndex: number;
  /** Whether this cell is currently selected for rotation. */
  active: boolean;
}

/** Configuration for glyph rotation behavior. */
export class GlyphConfig {
  /** Percentage of rotatable cells active at any time (0-100, default 20). */
  rotationPercent = 20;
  /** Time window in milliseconds for rotation spread (default 200). */
  rotationWindowMs = 200;
  /** Glyph palette — characters to rotate through. */
  palette: string[] = [
    ".",
    "*",
    "~",
    "^",
    ":",
    ";",
    "`",
    ",",
    "#",
    "+",
    "=",
    "/",
    "\\",
    "|",
    "{",
    "}",
    "<",
    ">",
  ];
  /** Foreground color for rotated glyphs. */
  fg: Color = new Color(20, 100, 20);
  /** Background color for rotated glyphs. */
  bg: Color = Color.black();
}

/**
 * Manages staggered glyph rotation across a frame buffer.
 *
 * Each cell has its own rotation timer, creating an organic wave-like
 * animation rather than synchronized flashing.
 */
export class GlyphRotator {
  private config: GlyphConfig;
  private states: CellState[][];
  private readonly _width: number;
  private readonly _height: number;
  private rng: Rng;

  /**
   * Create a new rotator for the given dimensions.
   *
   * The seed parameter makes animation deterministic for testing. Each cell
   * gets a randomized initial nextRotateAt spread across the rotation window,
   * and rotationPercent% of cells are marked active.
   */
  constructor(width: number, height: number, config: GlyphConfig, seed = 42) {
    this._width = width;
    this._height = height;
    this.config = config;
    this.rng = new Rng(seed);
    this.states = [];

    for (let r = 0; r < height; r++) {
      const row: CellState[] = [];
      for (let c = 0; c < width; c++) {
        const nextRotateAt = this.rng.nextBounded(config.rotationWindowMs);
        const paletteIndex =
          config.palette.length > 0 ? this.rng.nextBounded(config.palette.length) : 0;
        const active = this.rng.nextPercent() < config.rotationPercent;
        row.push({ nextRotateAt, paletteIndex, active });
      }
      this.states.push(row);
    }
  }

  /**
   * Advance the rotation given the current timestamp.
   *
   * For each active cell whose nextRotateAt <= nowMs:
   * 1. Advance the palette index.
   * 2. Write the new glyph to the buffer.
   * 3. Schedule the next rotation at nowMs + random offset.
   * 4. Re-select which cells are active.
   *
   * Returns the number of cells that actually rotated this tick.
   */
  tick(nowMs: number, buffer: FrameBuffer): number {
    if (this.config.palette.length === 0) return 0;

    let rotated = 0;
    const paletteLen = this.config.palette.length;

    for (let r = 0; r < this._height; r++) {
      for (let c = 0; c < this._width; c++) {
        const state = this.states[r][c];
        if (!state.active) continue;
        if (state.nextRotateAt > nowMs) continue;

        // Advance palette
        state.paletteIndex = (state.paletteIndex + 1) % paletteLen;
        const ch = this.config.palette[state.paletteIndex];

        // Write to buffer
        buffer.set(r, c, ch, this.config.fg, this.config.bg);

        // Schedule next rotation
        const offset = this.rng.nextBounded(this.config.rotationWindowMs);
        state.nextRotateAt = nowMs + offset;

        rotated++;
      }
    }

    // Re-select active cells for next round
    this.reselectActive();

    return rotated;
  }

  /** Re-select which cells are active based on rotationPercent. */
  private reselectActive(): void {
    for (const row of this.states) {
      for (const state of row) {
        state.active = this.rng.nextPercent() < this.config.rotationPercent;
      }
    }
  }

  /**
   * Update the rotation configuration.
   * Changes take effect on the next tick() call.
   * Palette indices are clamped to the new palette length.
   */
  setConfig(config: GlyphConfig): void {
    const newPaletteLen = config.palette.length;
    this.config = config;

    // Clamp palette indices
    if (newPaletteLen > 0) {
      for (const row of this.states) {
        for (const state of row) {
          if (state.paletteIndex >= newPaletteLen) {
            state.paletteIndex = 0;
          }
        }
      }
    }
  }

  /** Get the current configuration. */
  getConfig(): GlyphConfig {
    return this.config;
  }

  /** Get the grid dimensions as [width, height]. */
  getDimensions(): [number, number] {
    return [this._width, this._height];
  }

  /** Count the number of currently active cells. */
  activeCount(): number {
    let count = 0;
    for (const row of this.states) {
      for (const state of row) {
        if (state.active) count++;
      }
    }
    return count;
  }
}
